import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { permissionService } from '../services/permissionService';

interface PermissionContextType {
  isSuperAdmin?: boolean;
  hasPermission: (pageName: string, action: 'read' | 'write' | 'delete') => boolean;
  hasAdminPermission: (pageName: string) => boolean;
  hasPermissionById: (permissionId: string) => boolean;
  isLoading: boolean;
}

const PermissionContext = createContext<PermissionContextType>({
  isSuperAdmin: false,
  hasPermission: () => false,
  hasAdminPermission: () => false,
  hasPermissionById: () => false,
  isLoading: true
});

export const usePermission = () => useContext(PermissionContext);

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, permissions, isLoading } = useAuth();
  const [permissionServiceReady, setPermissionServiceReady] = useState(false);
  const [stagePermissions, setStagePermissions] = useState<string[]>([]);
  
  useEffect(() => {
    const initializePermissionService = async () => {
      if (user?.isadmin) {
        setPermissionServiceReady(true);
      } else if (permissions && permissions.length > 0) {
        await permissionService.initializeCache();
        setPermissionServiceReady(true);
      } else if (permissions && permissions.length === 0) {
        setPermissionServiceReady(true);
      }
    };

    initializePermissionService();
  }, [permissions, user?.isadmin]);

  useEffect(() => {
    const loadStagePermissions = () => {
      try {
        const storedStagePermissions = localStorage.getItem('stagePermissions');
        if (storedStagePermissions) {
          const parsedPermissions = JSON.parse(storedStagePermissions);
          setStagePermissions(parsedPermissions);
        }
      } catch (error) {
        console.error("Failed to load stage permissions from localStorage:", error);
        setStagePermissions([]);
      }
    };

    loadStagePermissions();

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'stagePermissions') {
        loadStagePermissions();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const hasPermission = (pageName: string, action: 'read' | 'write' | 'delete'): boolean => {
    if (user?.isadmin) {
      return true;
    }

    if (!permissionServiceReady) {
      return false;
    }

    // Client users only have read permissions
    if (user?.isclient && action !== 'read') {
      return false;
    }

    const permissionName = `${pageName}.${action}`;
    
    if (permissions && permissions.length > 0) {
      if (permissionService.hasPermissionByName(permissions, permissionName)) {
        return true;
      }
    }
    
    if (stagePermissions && stagePermissions.length > 0) {
      return permissionService.hasPermissionByName(stagePermissions, permissionName);
    }

    return false;
  };

  const hasAdminPermission = (pageName: string): boolean => {
    // Client users never have admin permissions
    if (user?.isclient) {
      return false;
    }

    if (user?.isadmin) {
      return true;
    }

    if (!permissionServiceReady) {
      return false;
    }

    if (permissions && permissions.length > 0) {
      if (permissionService.hasAdminPermissionByName(permissions, pageName)) {
        return true;
      }
    }
    
    if (stagePermissions && stagePermissions.length > 0) {
      return permissionService.hasAdminPermissionByName(stagePermissions, pageName);
    }

    return false;
  };

  const hasPermissionById = (permissionId: string): boolean => {
    if (user?.isadmin) {
      return true;
    }

    if (permissions && permissions.length > 0) {
      if (permissionService.hasPermissionById(permissions, permissionId)) {
        return true;
      }
    }
    
    if (stagePermissions && stagePermissions.length > 0) {
      return permissionService.hasPermissionById(stagePermissions, permissionId);
    }

    return false;
  };

  const isSuperAdmin = user?.isadmin || false;

  return (
    <PermissionContext.Provider value={{ 
      hasPermission, 
      hasAdminPermission,
      hasPermissionById,
      isSuperAdmin,
      isLoading: isLoading || !permissionServiceReady 
    }}>
      {children}
    </PermissionContext.Provider>
  );
};
